import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { User, Mail, Shield, Calendar, Layers, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ApexLoader from "@/components/ApexLoader";

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      setState({ loading: false, ok: false, error: "New password must be at least 8 characters." });
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setState({ loading: false, ok: false, error: "New password and confirmation don't match." });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setState({ loading: false, ok: true, error: "" });
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err) });
    }
  };

  return (
    <div className="bg-[#121214] border border-white/5 p-6 md:p-8" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-6">
        <Lock size={16} className="text-[#dc2626]" strokeWidth={1.5} />
        <div>
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500">security</div>
          <h2 className="font-display text-2xl text-white tracking-tight">Change password</h2>
        </div>
      </div>

      {state.ok && (
        <div className="mb-5 flex items-center gap-2 px-4 py-3 border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 font-mono-apex text-xs uppercase tracking-[0.2em]"
             data-testid="password-success">
          <CheckCircle2 size={14} /> Password updated.
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
            Current password
          </label>
          <input
            type="password"
            value={form.current_password}
            onChange={onChange("current_password")}
            autoComplete="current-password"
            className="input-apex w-full px-4 py-3 font-body text-sm"
            data-testid="current-password-input"
          />
        </div>
        <div>
          <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
            New password
          </label>
          <input
            type="password"
            value={form.new_password}
            onChange={onChange("new_password")}
            autoComplete="new-password"
            placeholder="Min 8 characters"
            className="input-apex w-full px-4 py-3 font-body text-sm"
            data-testid="new-password-input"
          />
        </div>
        <div>
          <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
            Confirm new password
          </label>
          <input
            type="password"
            value={form.confirm_password}
            onChange={onChange("confirm_password")}
            autoComplete="new-password"
            className="input-apex w-full px-4 py-3 font-body text-sm"
            data-testid="confirm-password-input"
          />
        </div>

        {state.error && (
          <div className="flex items-start gap-2 font-mono-apex text-xs text-[#dc2626]" data-testid="password-error">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{state.error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={state.loading}
          className="inline-flex items-center gap-2 btn-primary-apex px-6 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em] disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="change-password-btn"
        >
          {state.loading ? <><ApexLoader inline size={14} /> Updating…</> : "Update password"}
        </button>
      </form>
    </div>
  );
}

export default function DashboardSettings() {
  const { user } = useAuth();

  const fields = [
    { Icon: User, label: "Name", value: user?.name },
    { Icon: Mail, label: "Email", value: user?.email },
    { Icon: Shield, label: "Role", value: user?.role },
    { Icon: Layers, label: "Auth Provider", value: user?.auth_provider },
    {
      Icon: Calendar,
      label: "Provisioned",
      value: user?.created_at ? new Date(user.created_at).toLocaleString() : "—",
    },
  ];

  const canChangePassword = user?.auth_provider === "local" || !user?.auth_provider;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">system · settings</div>
        <h1 className="font-display text-4xl text-white tracking-tight">
          Operator <em className="silver-text not-italic">profile</em>.
        </h1>
      </div>

      <div className="bg-[#121214] border border-white/5 divide-y divide-white/5">
        {fields.map((f) => (
          <div key={f.label} className="flex items-center gap-4 px-6 py-5">
            <f.Icon size={16} className="text-[#dc2626]" strokeWidth={1.5} />
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 w-40">
              {f.label}
            </div>
            <div className="font-body text-sm text-white flex-1 break-all">{f.value || "—"}</div>
          </div>
        ))}
      </div>

      {canChangePassword ? (
        <ChangePasswordCard />
      ) : (
        <div className="bg-[#121214] border border-white/5 p-6 font-body text-sm text-zinc-400">
          You signed in via {user?.auth_provider}. Password changes are managed by that provider.
        </div>
      )}

      <div className="bg-[#121214] border border-white/5 p-6">
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
          system status
        </div>
        <div className="font-body text-sm text-zinc-300 leading-relaxed">
          All telemetry pipelines operational. Sentinel core latency within nominal range. Next
          scheduled calibration window: <span className="font-mono-apex text-white">Sunday · 03:00 UTC</span>.
        </div>
      </div>
    </div>
  );
}
